import React, { useRef } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface CityPropertyCardProps {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  url: string;
  floorPlansUrl: string;
  neighborhoodUrl: string;
  amenitySnippet: string;
  startingPrice: number | null;
  images: string[];
  onMapPinClick?: () => void;
}

const LocationDot = () => (
  <svg viewBox="0 0 384 512" width="20" height="20" fill="currentColor">
    <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
  </svg>
);

const ImageIcon = () => (
  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor">
    <path d="M0 96C0 60.7 28.7 32 64 32H448c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zM323.8 202.5c-4.5-6.6-11.9-10.5-19.8-10.5s-15.4 3.9-19.8 10.5l-87 127.6L170.7 297c-4.6-5.7-11.5-9-18.7-9s-14.2 3.3-18.7 9l-64 80c-5.8 7.2-6.9 17.1-2.9 25.4s12.4 13.6 21.6 13.6h96 32H424c8.9 0 17.1-4.9 21.2-12.8s3.6-17.4-1.4-24.7l-120-176zM112 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z" />
  </svg>
);

const ChevronLeft = () => (
  <svg viewBox="0 0 320 512" width="14" height="14" fill="currentColor">
    <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 320 512" width="14" height="14" fill="currentColor">
    <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
  </svg>
);

export function CityPropertyCard({
  name, address, city, state, zip, phone, url, floorPlansUrl, neighborhoodUrl,
  amenitySnippet, startingPrice, images, onMapPinClick,
}: CityPropertyCardProps) {
  const sliderRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: number) => {
    if (!sliderRef.current) return;
    const w = sliderRef.current.offsetWidth;
    sliderRef.current.scrollBy({ left: w * dir, behavior: 'smooth' });
  };

  const displayImages = images.length > 0 ? images : [];

  return (
    <div className="case-market-elem">
      <div className="case-market-elem-info">
        <h2>
          <a href={url}>{name}</a>
        </h2>
        <p className="case-market-elem-address">
          {address},<br />
          {city}, {state} {zip}<br />
          <a href={`tel:${phone.replace(/[^0-9]/g, '')}`} className="case-market-elem-phone">{phone}</a>
        </p>
        <p>
          <a href={neighborhoodUrl} className="case-market-elem-neighborhood">
            View Neighborhoods
          </a>
        </p>
        {onMapPinClick && (
          <button type="button" className="case-market-elem-map-pin" onClick={onMapPinClick}>
            <LocationDot />
          </button>
        )}
      </div>

      <div className="case-market-elem-box">
        <div className="case-market-elem-inner">
          {amenitySnippet && (
            <p className="case-market-elem-amenity">{amenitySnippet}</p>
          )}
          <a href={floorPlansUrl} className="case-market-elem-cta">
            {startingPrice != null
              ? `Starting At $${startingPrice.toLocaleString()}`
              : 'View Pricing'}
          </a>
        </div>
      </div>

      {displayImages.length > 0 && (
        <div className="case-market-elem-slider">
          <div className="case-market-elem-slider-track" ref={sliderRef}>
            {displayImages.map((img, i) => (
              <VibesitesImage key={i} url={img} alt={`${name} photo ${i + 1}`} sizes="(min-width: 992px) 50vw, 100vw" />
            ))}
          </div>
          <button type="button" className="case-market-gallery-trigger">
            <ImageIcon /> <span>More Photos</span>
          </button>
          <button type="button" className="case-market-mobile-pin" onClick={onMapPinClick}>
            <LocationDot />
          </button>
          {displayImages.length > 1 && (
            <>
              <button type="button" className="case-market-elem-slider-prev" onClick={() => scroll(-1)}>
                <ChevronLeft />
              </button>
              <button type="button" className="case-market-elem-slider-next" onClick={() => scroll(1)}>
                <ChevronRight />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CityPropertyCard;
